import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Pencil, Trash2, Settings } from 'lucide-react';

type NavigationPermissions = {
  browse?: boolean;
  campaigns?: boolean;
  longTermListings?: boolean;
  reservations?: boolean;
  export?: boolean;
};

type Brand = {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  tenderPricePerSKUPerMonth?: number;
  navigationPermissions?: NavigationPermissions | string; // Can be object or JSON string
  showLongTermPopup?: boolean;
  createdAt: string;
  updatedAt: string;
};

const NAVIGATION_ITEMS = [
  { key: 'browse', label: 'Browse', path: '/' },
  { key: 'campaigns', label: 'Campaigns', path: '/calculator' },
  { key: 'longTermListings', label: 'Long-Term Listings', path: '/new-location-tender' },
  { key: 'reservations', label: 'My Reservations', path: '/reservations' },
  { key: 'export', label: 'Machine List Export', path: '/export' },
];

export default function AdminBrands() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [createForm, setCreateForm] = useState({ 
    name: '', 
    description: '', 
    isActive: true,
    navigationPermissions: {
      browse: true,
      campaigns: true,
      longTermListings: true,
      reservations: true,
      export: true,
    },
    showLongTermPopup: false,
  });
  const [editForm, setEditForm] = useState({ 
    name: '', 
    description: '', 
    isActive: true, 
    tenderPricePerSKU: '',
    navigationPermissions: {
      browse: true,
      campaigns: true,
      longTermListings: true,
      reservations: true,
      export: true,
    },
    showLongTermPopup: false,
  });

  async function loadBrands() {
    try {
      const res = await fetch('/api/admin/brands', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setBrands(data);
      }
    } catch (error) {
      console.error('Failed to load brands:', error);
    }
  }

  useEffect(() => {
    loadBrands();
  }, []);

  async function handleCreate() {
    try {
      const payload = {
        name: createForm.name,
        description: createForm.description,
        isActive: createForm.isActive,
        navigationPermissions: JSON.stringify(createForm.navigationPermissions),
        showLongTermPopup: createForm.showLongTermPopup,
      };

      const res = await fetch('/api/admin/brands', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setOpenCreate(false);
        setCreateForm({ 
          name: '', 
          description: '', 
          isActive: true,
          navigationPermissions: {
            browse: true,
            campaigns: true,
            longTermListings: true,
            reservations: true,
            export: true,
          },
          showLongTermPopup: false,
        });
        loadBrands();
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to create brand');
      }
    } catch (error) {
      console.error('Failed to create brand:', error);
      alert('Failed to create brand');
    }
  }

  async function handleEdit() {
    if (!selectedBrand) return;

    try {
      const payload = {
        name: editForm.name,
        description: editForm.description,
        isActive: editForm.isActive,
        tenderPricePerSKUPerMonth: editForm.tenderPricePerSKU ? Math.round(parseFloat(editForm.tenderPricePerSKU) * 100) : undefined,
        navigationPermissions: JSON.stringify(editForm.navigationPermissions),
        showLongTermPopup: editForm.showLongTermPopup,
      };

      const res = await fetch(`/api/admin/brands/${selectedBrand.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setOpenEdit(false);
        setSelectedBrand(null);
        loadBrands();
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to update brand');
      }
    } catch (error) {
      console.error('Failed to update brand:', error);
      alert('Failed to update brand');
    }
  }

  async function handleDelete() {
    if (!selectedBrand) return;

    try {
      const res = await fetch(`/api/admin/brands/${selectedBrand.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        setOpenDelete(false);
        setSelectedBrand(null);
        loadBrands();
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to delete brand');
      }
    } catch (error) {
      console.error('Failed to delete brand:', error);
      alert('Failed to delete brand');
    }
  }

  function openEditDialog(brand: Brand) {
    setSelectedBrand(brand);
    
    // Parse navigationPermissions if it's a string
    let navPermissions: NavigationPermissions = {
      browse: true,
      campaigns: true,
      longTermListings: true,
      reservations: true,
      export: true,
    };
    
    if (brand.navigationPermissions) {
      try {
        navPermissions = typeof brand.navigationPermissions === 'string' 
          ? JSON.parse(brand.navigationPermissions)
          : brand.navigationPermissions;
        // Ensure all keys exist with defaults
        navPermissions = {
          browse: navPermissions.browse ?? true,
          campaigns: navPermissions.campaigns ?? true,
          longTermListings: navPermissions.longTermListings ?? true,
          reservations: navPermissions.reservations ?? true,
          export: navPermissions.export ?? true,
        };
      } catch (e) {
        console.error('Error parsing navigationPermissions:', e);
      }
    }
    
    setEditForm({
      name: brand.name,
      description: brand.description || '',
      isActive: brand.isActive,
      tenderPricePerSKU: brand.tenderPricePerSKUPerMonth ? (brand.tenderPricePerSKUPerMonth / 100).toFixed(2) : '',
      navigationPermissions: navPermissions as Required<NavigationPermissions>,
      showLongTermPopup: brand.showLongTermPopup || false,
    });
    setOpenEdit(true);
  }

  function openDeleteDialog(brand: Brand) {
    setSelectedBrand(brand);
    setOpenDelete(true);
  }

  function navigateToMachineAssignment(brandId: string) {
    navigate(`/admin/brands/${brandId}/machines`);
  }

  return (
    <div className="h-full bg-slate-50">
      <div className="w-full px-2 md:px-4 py-4 md:py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Brand Management</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage brands and assign machines to them
            </p>
          </div>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button>Create Brand</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Brand</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Brand name"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <textarea
                    id="description"
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                    placeholder="Brand description"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={createForm.isActive}
                    onChange={(e) => setCreateForm({ ...createForm, isActive: e.target.checked })}
                  />
                  <Label htmlFor="isActive">Active</Label>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <Label className="text-sm font-semibold mb-3 block">Navigation Access</Label>
                  <p className="text-xs text-gray-500 mb-3">Select which sections users from this brand can access:</p>
                  <div className="space-y-2">
                    {NAVIGATION_ITEMS.map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`nav-create-${item.key}`}
                          checked={createForm.navigationPermissions[item.key as keyof typeof createForm.navigationPermissions] ?? true}
                          onChange={(e) => setCreateForm({
                            ...createForm,
                            navigationPermissions: {
                              ...createForm.navigationPermissions,
                              [item.key]: e.target.checked,
                            },
                          })}
                        />
                        <Label htmlFor={`nav-create-${item.key}`} className="text-sm cursor-pointer">
                          {item.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="showPopup"
                        checked={createForm.showLongTermPopup}
                        onChange={(e) => setCreateForm({ ...createForm, showLongTermPopup: e.target.checked })}
                      />
                      <Label htmlFor="showPopup" className="text-sm cursor-pointer">
                        Show popup directing users to Long-Term Listings on login
                      </Label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      Users will see a popup message directing them to the Long-Term Listings section when they log in
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setOpenCreate(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={!createForm.name}>
                    Create Brand
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {brands.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-12 text-center shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">No brands yet</h3>
            <p className="mt-2 text-sm text-slate-500">
              Get started by creating your first brand.
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-slate-900">{brand.name}</h3>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        brand.isActive
                          ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                          : 'bg-slate-100 text-slate-700 border border-slate-200'
                      }`}
                    >
                      {brand.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {brand.description && (
                    <p className="mt-2 text-sm text-slate-600">{brand.description}</p>
                  )}
                  <p className="mt-2 text-xs text-slate-400">
                    Created: {new Date(brand.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateToMachineAssignment(brand.id)}
                    title="Manage machine assignments"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Assign Machines
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(brand)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDeleteDialog(brand)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={openEdit} onOpenChange={setOpenEdit}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Brand</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <textarea
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-isActive"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                />
                <Label htmlFor="edit-isActive">Active</Label>
              </div>
              <div className="border-t pt-4 mt-2">
                <Label htmlFor="edit-tenderPrice">Tender Price per SKU per Month (£)</Label>
                <Input
                  id="edit-tenderPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.tenderPricePerSKU}
                  onChange={(e) => setEditForm({ ...editForm, tenderPricePerSKU: e.target.value })}
                  placeholder="e.g., 150.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Price charged per SKU per machine per month for tender/new location requests
                </p>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-semibold mb-3 block">Navigation Access</Label>
                <p className="text-xs text-gray-500 mb-3">Select which sections users from this brand can access:</p>
                <div className="space-y-2">
                  {NAVIGATION_ITEMS.map((item) => (
                    <div key={item.key} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`nav-edit-${item.key}`}
                        checked={editForm.navigationPermissions[item.key as keyof typeof editForm.navigationPermissions] ?? true}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          navigationPermissions: {
                            ...editForm.navigationPermissions,
                            [item.key]: e.target.checked,
                          },
                        })}
                      />
                      <Label htmlFor={`nav-edit-${item.key}`} className="text-sm cursor-pointer">
                        {item.label}
                      </Label>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-showPopup"
                      checked={editForm.showLongTermPopup}
                      onChange={(e) => setEditForm({ ...editForm, showLongTermPopup: e.target.checked })}
                    />
                    <Label htmlFor="edit-showPopup" className="text-sm cursor-pointer">
                      Show popup directing users to Long-Term Listings on login
                    </Label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Users will see a popup message directing them to the Long-Term Listings section when they log in
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpenEdit(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEdit} disabled={!editForm.name}>
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={openDelete} onOpenChange={setOpenDelete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Brand</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-slate-600">
                Are you sure you want to delete the brand <strong>{selectedBrand?.name}</strong>?
                This action cannot be undone.
              </p>
              {selectedBrand && !selectedBrand.isActive && (
                <p className="mt-2 text-xs text-slate-500">
                  This brand is already inactive. You can delete it permanently.
                </p>
              )}
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setOpenDelete(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  Delete Brand
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

